import React, { memo, useEffect, useRef, useState } from 'react';
import {
  isArrayVariable,
  isNumberVariable,
  normalizeArrayValues,
  normalizeNumberValue,
  normalizeVariableName,
  sanitizeNumberInput,
  VARIABLE_COLORS,
  VARIABLE_TYPE_NUMBER,
} from '../../../../utils/variableScope';
import { containsMathValueToken } from '../../../../utils/mathValueToken';
import NestedVarField from '../../NestedVarField';
import {
  NUMBER_VALUE_SLASH_IDS,
  TEXT_VALUE_SLASH_IDS,
} from '../../valueSlash';
import ArrayValuesCell from './ArrayValuesCell';
import PathSlashUndo from './PathSlashUndo';
import ColorDot from './ColorDot';
import LocalField from './LocalField';
import LockIcon from './LockIcon';
import ScopeSelect from './ScopeSelect';
import ScopeTip from './ScopeTip';
import TypeSelect from './TypeSelect';

const VariableRow = memo(({
  variable,
  scope,
  sourceName,
  conflictSource,
  readOnly,
  /** Inherited rows: offer value edit (gated by confirm) when host can patch source. */
  canEditInheritedValue = false,
  inheritedValueUnlocked = false,
  onRequestInheritedValueEdit = null,
  onPatchInheritedValue = null,
  autoFocusValue = false,
  onValueFocused = null,
  showScopeSelect,
  scopeChoice = null,
  scopeOptions,
  onPatch,
  onRename,
  onScopeChange,
  onTypeChange,
  onDelete,
  onToggleLock,
  autoFocus,
  dragHandleProps = null,
  setSortableRef = null,
  sortableStyle = null,
  resolveVariable = null,
  pickerVariables = [],
  onCreateVariable = null,
  historyEpoch = 0,
  pathSlashOffers = [],
  onUndoPathSlash = null,
}) => {
  const nameRef = useRef(null);
  // Name at focus — props stay on the committed name until blur/Enter commits.
  const nameAtFocusRef = useRef(variable.name);
  const scopePillRef = useRef(null);
  const accent = variable.color || VARIABLE_COLORS[0];
  const locked = !!variable.locked;
  const asArray = isArrayVariable(variable);
  const asNumber = isNumberVariable(variable);
  const arraySlots = asArray ? normalizeArrayValues(variable.values) : [];
  const arrayBaseName = normalizeVariableName(variable.name) || 'name';
  // Lock = survive campaign reset + freeze structure/delete — values stay editable
  // so you can update a locked default without unlock → edit → re-lock.
  // Inherited rows stay value-readonly until the user confirms remote edit.
  const valueReadOnly =
    readOnly && !(canEditInheritedValue && inheritedValueUnlocked);
  const metaReadOnly = readOnly || locked;
  const [scopeTipRect, setScopeTipRect] = useState(null);
  const [scopeMenuOpen, setScopeMenuOpen] = useState(false);
  /** Heights of value slots — keeps `[n]}}` closers aligned as fields grow. */
  const [arraySlotHeights, setArraySlotHeights] = useState([]);

  useEffect(() => {
    if (!asArray) setArraySlotHeights([]);
  }, [asArray]);
  const inheritedValuePending =
    canEditInheritedValue && !inheritedValueUnlocked && !!onRequestInheritedValueEdit;
  const inheritedInviteTitle = inheritedValuePending
    ? `Defined on “${sourceName || 'parent campaign'}” — click to edit`
    : undefined;
  const requestInheritedEdit = (e) => {
    if (!inheritedValuePending) return;
    e.preventDefault();
    onRequestInheritedValueEdit();
  };

  useEffect(() => {
    if (autoFocus && !readOnly && !locked) nameRef.current?.focus();
  }, [autoFocus, readOnly, locked]);

  const selectedScope = scopeOptions.some((o) => o.value === scopeChoice)
    ? scopeChoice
    : scope;
  const scopeLabel =
    scope === 'inherited'
      ? sourceName || 'Inherited'
      : scopeOptions.find((o) => o.value === selectedScope)?.label || selectedScope;
  const pillScope = scope === 'inherited' ? 'inherited' : selectedScope;

  const showScopeTip = () => {
    const el = scopePillRef.current;
    if (!el) return;
    setScopeTipRect(el.getBoundingClientRect());
  };

  const commitValuePatch = (patch, opts) => {
    if (canEditInheritedValue && onPatchInheritedValue) {
      onPatchInheritedValue(patch, opts);
      return;
    }
    onPatch(scope, variable.id, patch, opts);
  };

  return (
    <div
      ref={setSortableRef}
      className={[
        'variables-panel__table-row',
        readOnly ? 'variables-panel__table-row--readonly' : '',
        locked ? 'variables-panel__table-row--locked' : '',
        conflictSource ? 'variables-panel__table-row--conflict' : '',
        inheritedValuePending ? 'variables-panel__table-row--inherited-editable' : '',
        asArray ? 'variables-panel__table-row--array' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      style={{ '--var-row-color': accent, ...(sortableStyle || {}) }}
    >
      <div className="variables-panel__cell variables-panel__cell--drag">
        {dragHandleProps ? (
          <button
            type="button"
            className="variables-panel__drag-handle"
            title="Drag to reorder"
            aria-label="Drag to reorder variable"
            style={{ touchAction: 'none' }}
            {...dragHandleProps.attributes}
            {...dragHandleProps.listeners}
          >
            <span className="variables-panel__drag-dots" aria-hidden="true">
              <span className="variables-panel__drag-dot" />
              <span className="variables-panel__drag-dot" />
              <span className="variables-panel__drag-dot" />
              <span className="variables-panel__drag-dot" />
              <span className="variables-panel__drag-dot" />
              <span className="variables-panel__drag-dot" />
            </span>
          </button>
        ) : null}
      </div>

      <div className="variables-panel__cell variables-panel__cell--color">
        <ColorDot
          color={variable.color}
          readOnly={metaReadOnly}
          onChange={(color) => onPatch(scope, variable.id, { color })}
        />
      </div>

      <div
        className="variables-panel__cell variables-panel__cell--name"
        title={inheritedInviteTitle}
        onMouseDown={requestInheritedEdit}
      >
        <div
          className={[
            'variables-panel__name-col',
            asArray ? 'variables-panel__name-col--array' : '',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          {asArray ? (
            <div className="variables-panel__name-array">
              <div className="variables-panel__name-array-open">
                <div className="variables-panel__name-wrap variables-panel__name-wrap--array">
                  <span className="variables-panel__brace">{'{{'}</span>
                  <LocalField
                    inputRef={nameRef}
                    className={[
                      'variables-panel__input--name',
                      inheritedValuePending ? 'is-inherited-editable' : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    value={variable.name}
                    placeholder="name"
                    readOnly={metaReadOnly}
                    spellCheck={false}
                    transform={(v) => v.replace(/[{}]/g, '').replace(/\s+/g, '_')}
                    onFocus={() => {
                      nameAtFocusRef.current = variable.name;
                    }}
                    onCommit={(name) => {
                      const from = String(nameAtFocusRef.current || '');
                      if (onRename && from && from !== String(name || '')) {
                        onRename(scope, variable.id, from, name);
                        return;
                      }
                      onPatch(scope, variable.id, { name }, { silent: false });
                    }}
                    ariaLabel="Variable name"
                    historyEpoch={historyEpoch}
                  />
                </div>
              </div>
              <div className="variables-panel__name-array-closes" aria-hidden="true">
                {arraySlots.map((_, i) => (
                  <div
                    key={i}
                    className="variables-panel__name-array-close"
                    title={`{{${arrayBaseName}[${i + 1}]}}`}
                    style={
                      arraySlotHeights[i]
                        ? { height: arraySlotHeights[i] }
                        : undefined
                    }
                  >
                    <span className="variables-panel__array-close-index">[{i + 1}]</span>
                    <span className="variables-panel__array-close-brace">{'}}'}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="variables-panel__name-wrap">
              <span className="variables-panel__brace">{'{{'}</span>
              <LocalField
                inputRef={nameRef}
                className={[
                  'variables-panel__input--name',
                  inheritedValuePending ? 'is-inherited-editable' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                value={variable.name}
                placeholder="name"
                readOnly={metaReadOnly}
                spellCheck={false}
                transform={(v) => v.replace(/[{}]/g, '').replace(/\s+/g, '_')}
                onFocus={() => {
                  nameAtFocusRef.current = variable.name;
                }}
                onCommit={(name) => {
                  const from = String(nameAtFocusRef.current || '');
                  if (onRename && from && from !== String(name || '')) {
                    onRename(scope, variable.id, from, name);
                    return;
                  }
                  onPatch(scope, variable.id, { name }, { silent: false });
                }}
                ariaLabel="Variable name"
                historyEpoch={historyEpoch}
              />
              <span className="variables-panel__brace">{'}}'}</span>
            </div>
          )}
          {conflictSource && (
            <div className="variables-panel__conflict" role="alert">
              Already set by &ldquo;{conflictSource}&rdquo; — higher level wins
            </div>
          )}
        </div>
      </div>

      <div className="variables-panel__cell variables-panel__cell--type">
        <TypeSelect
          variable={variable}
          readOnly={metaReadOnly}
          onChange={(type) => onTypeChange?.(scope, variable, type)}
        />
      </div>

      <div
        className="variables-panel__cell variables-panel__cell--value"
        title={inheritedInviteTitle}
        onMouseDown={requestInheritedEdit}
      >
        {asArray ? (
          <ArrayValuesCell
            name={variable.name}
            values={variable.values}
            readOnly={valueReadOnly}
            className={inheritedValuePending ? 'is-inherited-editable' : ''}
            autoFocusFirst={autoFocusValue}
            onFocusFirst={() => onValueFocused?.()}
            onCommitValues={(values, opts) =>
              commitValuePatch({ values, type: 'array', value: '' }, opts)
            }
            onSlotHeights={setArraySlotHeights}
            resolveVariable={resolveVariable}
            pickerVariables={pickerVariables}
            onCreateVariable={valueReadOnly ? null : onCreateVariable}
            historyEpoch={historyEpoch}
            pathSlashOffers={pathSlashOffers}
            onUndoPathSlash={onUndoPathSlash}
          />
        ) : asNumber && containsMathValueToken(variable.value) ? (
          <NestedVarField
            className={[
              'variables-panel__input--value',
              inheritedValuePending ? 'is-inherited-editable' : '',
            ]
              .filter(Boolean)
              .join(' ')}
            value={variable.value}
            placeholder="0"
            readOnly={valueReadOnly}
            autoFocus={autoFocusValue}
            onFocus={() => onValueFocused?.()}
            onCommit={(value, opts) =>
              commitValuePatch(
                {
                  value: containsMathValueToken(value)
                    ? value
                    : normalizeNumberValue(value),
                  type: VARIABLE_TYPE_NUMBER,
                },
                opts
              )
            }
            ariaLabel="Variable value"
            resolveVariable={resolveVariable}
            pickerVariables={pickerVariables}
            onCreateVariable={valueReadOnly ? null : onCreateVariable}
            historyEpoch={historyEpoch}
            slashAllowed={NUMBER_VALUE_SLASH_IDS}
          />
        ) : asNumber ? (
          <LocalField
            className={[
              'variables-panel__input--value',
              'variables-panel__input--number',
              inheritedValuePending ? 'is-inherited-editable' : '',
            ]
              .filter(Boolean)
              .join(' ')}
            value={variable.value}
            placeholder="0"
            readOnly={valueReadOnly}
            spellCheck={false}
            inputMode="decimal"
            autoFocus={autoFocusValue}
            transform={sanitizeNumberInput}
            onFocus={() => onValueFocused?.()}
            onCommit={(value, opts) =>
              commitValuePatch(
                {
                  value: containsMathValueToken(value)
                    ? value
                    : normalizeNumberValue(value),
                  type: VARIABLE_TYPE_NUMBER,
                },
                opts
              )
            }
            ariaLabel="Variable value"
            historyEpoch={historyEpoch}
            allowMath
            resolveVariable={resolveVariable}
            pickerVariables={pickerVariables}
            onCreateVariable={valueReadOnly ? null : onCreateVariable}
          />
        ) : (
          <div className="variables-panel__value-field">
            <NestedVarField
              className={[
                'variables-panel__input--value',
                inheritedValuePending ? 'is-inherited-editable' : '',
              ]
                .filter(Boolean)
                .join(' ')}
              value={variable.value}
              placeholder="value"
              readOnly={valueReadOnly}
              autoFocus={autoFocusValue}
              onFocus={() => onValueFocused?.()}
              onCommit={(value, opts) => commitValuePatch({ value }, opts)}
              ariaLabel="Variable value"
              resolveVariable={resolveVariable}
              pickerVariables={pickerVariables}
              onCreateVariable={valueReadOnly ? null : onCreateVariable}
              historyEpoch={historyEpoch}
              slashAllowed={TEXT_VALUE_SLASH_IDS}
            />
            {pathSlashOffers
              .filter((offer) => offer.slot == null)
              .map((offer) => (
                <PathSlashUndo key={offer.id} onUndo={() => onUndoPathSlash?.(offer)} />
              ))}
          </div>
        )}
      </div>

      <div
        className="variables-panel__cell variables-panel__cell--description"
        title={inheritedInviteTitle}
        onMouseDown={requestInheritedEdit}
      >
        <NestedVarField
          className={[
            'variables-panel__input--description',
            inheritedValuePending ? 'is-inherited-editable' : '',
          ]
            .filter(Boolean)
            .join(' ')}
          value={variable.description}
          placeholder={readOnly ? '' : 'optional note'}
          readOnly={metaReadOnly}
          onCommit={(description, opts) => onPatch(scope, variable.id, { description }, opts)}
          ariaLabel="Variable description"
          resolveVariable={resolveVariable}
          pickerVariables={pickerVariables}
          onCreateVariable={metaReadOnly ? null : onCreateVariable}
          historyEpoch={historyEpoch}
        />
      </div>

      <div className="variables-panel__cell variables-panel__cell--scope">
        <div
          ref={scopePillRef}
          className={`variables-panel__scope-pill variables-panel__scope-pill--${pillScope}`}
          data-scope={pillScope}
          aria-label={scopeLabel}
          onMouseEnter={scopeMenuOpen ? undefined : showScopeTip}
          onMouseLeave={() => setScopeTipRect(null)}
        >
          <span className="variables-panel__scope-dot" aria-hidden="true" />
          {showScopeSelect && !metaReadOnly ? (
            <ScopeSelect
              value={selectedScope}
              options={scopeOptions}
              label={scopeLabel}
              onOpenChange={(next) => {
                setScopeMenuOpen(next);
                if (next) setScopeTipRect(null);
              }}
              onChange={(next) => onScopeChange(selectedScope, variable.id, next)}
            />
          ) : (
            <span className="variables-panel__scope-label">{scopeLabel}</span>
          )}
        </div>
        {scopeTipRect && !scopeMenuOpen && (
          <ScopeTip anchorRect={scopeTipRect} text={scopeLabel} />
        )}
      </div>

      <div className="variables-panel__cell variables-panel__cell--actions">
        {!readOnly && onToggleLock && (
          <button
            type="button"
            className={`variables-panel__lock-btn${locked ? ' is-locked' : ''}`}
            onClick={() => onToggleLock(scope, variable.id)}
            title={
              locked
                ? 'Unlock — Empty all can clear this value'
                : 'Lock — Empty all skips this value'
            }
            aria-label={
              locked
                ? 'Unlock variable (allow Empty all to clear it)'
                : 'Lock variable (Empty all skips it)'
            }
            aria-pressed={locked}
          >
            <LockIcon locked={locked} />
          </button>
        )}
        {!readOnly && (
          <button
            type="button"
            className={`variables-panel__delete-btn${locked ? ' is-disabled' : ''}`}
            onClick={() => {
              if (locked) return;
              onDelete(scope, variable.id);
            }}
            disabled={locked}
            title={
              locked
                ? 'Unlock the variable before you can delete it'
                : 'Delete variable'
            }
            aria-label={
              locked
                ? 'Delete disabled while locked'
                : 'Delete variable'
            }
          >
            ×
          </button>
        )}
      </div>
    </div>
  );
});

export default VariableRow;